/**
 * Promo Code activation component
 */
import React, { useState } from 'react';
import axios from 'axios';
import { AuthContext } from '../../WebApp';

function PromoCode() {
  const { user, language, updateUser } = React.useContext(AuthContext);
  const [code, setCode] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState(25);
  const [plisioLoading, setPlisioLoading] = useState(false);
  const [plisioMessage, setPlisioMessage] = useState(null);

  const TOKENS_PER_DOLLAR = 200; // $5 = 1000 tokens baseline
  const estimatedTokens = Math.max(0, Math.floor((Number(topUpAmount) || 0) * TOKENS_PER_DOLLAR));

  const translations = {
    en: {
      title: 'Activate Promo Code',
      subtitle: 'Boost your balance in seconds',
      placeholder: 'Enter promo code',
      activate: 'Activate',
      success: 'Promo code activated successfully!',
      error: 'Failed to activate promo code',
      paymentTitle: 'Top up your balance',
      hint: 'Need help? DM us in Telegram @hotbump_bot',
      cardTitle: 'Pay with cards or crypto',
      amountLabel: 'Amount (USD)',
      payButton: 'Pay via Plisio',
      payDescription: 'You will be redirected to Plisio secure checkout. After the payment is confirmed, your token balance will refresh automatically.',
      minAmount: 'Minimum top-up is $5',
      promoBot: 'Need more promo codes? Text @hotbump_bot to get a custom pack.'
    },
    ru: {
      title: 'Активация промокода',
      subtitle: 'Пополните баланс за секунды',
      placeholder: 'Введите промокод',
      activate: 'Активировать',
      success: 'Промокод успешно активирован!',
      error: 'Ошибка активации промокода',
      paymentTitle: 'Способы пополнения',
      hint: 'Нужна помощь? Напишите нам в Telegram @hotbump_bot',
      cardTitle: 'Оплатить картой или криптой',
      amountLabel: 'Сумма (USD)',
      payButton: 'Оплатить через Plisio',
      payDescription: 'Мы перенаправим вас на защищённую страницу Plisio. После подтверждения платежа баланс токенов пополнится автоматически.',
      minAmount: 'Минимальная сумма пополнения — $5',
      promoBot: 'Нужны дополнительные промокоды? Напишите @hotbump_bot и мы подготовим индивидуальный пакет.'
    }
  };

  const t = translations[language] || translations.en;
  const conversionText = language === 'en'
    ? 'Regular price: $8 = 1000 tokens. Special discount: $5 = 1000 tokens (1$ = 200 tokens). After payment the system converts USD to tokens automatically.'
    : 'Обычная цена: 8$ = 1000 токенов. Специальная скидка: 5$ = 1000 токенов (1$ = 200 токенов). После оплаты система автоматически конвертирует USD в токены.';
  const estimatedLabel = language === 'en'
    ? `≈ ${estimatedTokens.toLocaleString('en-US')} tokens will be credited.`
    : `≈ ${estimatedTokens.toLocaleString('ru-RU')} токенов будет начислено.`;

  const handleActivate = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;

    setLoading(true);
    setFeedback(null);

    try {
      const response = await axios.post('/promo/activate', { code: code.toUpperCase() });
      setFeedback({ type: 'success', text: response.data.message || t.success });
      setCode('');

      if (user && updateUser) {
        const newBalance = response.data.new_balance ?? (user.token_balance || 0) + (response.data.tokens_added || 0);
        updateUser({ ...user, token_balance: newBalance });
      }
    } catch (error) {
      setFeedback({
        type: 'error',
        text: `${t.error}: ${error.response?.data?.detail || error.message}`
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    const numericAmount = Number(topUpAmount);
    if (Number.isNaN(numericAmount) || numericAmount < 5) {
      setPlisioMessage({
        type: 'error',
        text: language === 'en' ? 'Enter amount starting from $5' : 'Введите сумму от $5'
      });
      return;
    }
    setPlisioLoading(true);
    setPlisioMessage(null);
    try {
      const response = await axios.post('/payments/plisio/invoice', { amount_usd: numericAmount });
      window.location.href = response.data.invoice_url;
    } catch (error) {
      setPlisioMessage({
        type: 'error',
        text: error.response?.data?.detail || (language === 'en' ? 'Failed to create invoice' : 'Не удалось создать счёт')
      });
    } finally {
      setPlisioLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="text-white text-center py-12">
        {language === 'en' ? 'Please login to activate promo codes' : 'Пожалуйста, войдите для активации промокодов'}
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      <div className="rounded-3xl border border-white/10 bg-black/30 backdrop-blur px-6 sm:px-10 py-8 text-center">
        <h1 className="text-4xl font-bold text-white mb-3">{t.title}</h1>
        <p className="text-gray-300 text-base">{t.subtitle}</p>
      </div>

      <div className="space-y-8">
        <div className="rounded-2xl border border-burgundy-900/60 bg-dark-800/80 p-6 shadow-lg shadow-burgundy-900/20">
          <h2 className="text-2xl font-semibold text-white mb-4">{t.title}</h2>
          <form onSubmit={handleActivate} className="space-y-4">
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder={t.placeholder}
              className="w-full px-4 py-3 bg-dark-700 border border-burgundy-800 text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-burgundy-500"
            />

            <button
              type="submit"
              disabled={loading}
              className="w-full px-4 py-3 rounded-xl bg-burgundy-600 hover:bg-burgundy-500 text-white font-semibold transition disabled:opacity-60"
            >
              {loading ? '...' : t.activate}
            </button>
          </form>

          {feedback && (
            <div
              className={`mt-4 p-3 rounded-xl text-sm ${
                feedback.type === 'success'
                  ? 'bg-green-900/30 text-green-200 border border-green-600/40'
                  : 'bg-red-900/30 text-red-200 border border-red-600/40'
              }`}
            >
              {feedback.text}
            </div>
          )}

          <p className="mt-6 text-gray-400 text-sm">{t.hint}</p>
        </div>

        <div className="rounded-2xl border border-white/10 bg-black/30 backdrop-blur p-6 space-y-4">
          <h2 className="text-2xl font-semibold text-white">{t.cardTitle}</h2>
          <form onSubmit={handleCreateInvoice} className="space-y-4">
            <label className="block text-sm text-gray-300">
              {t.amountLabel}
              <input
                type="number"
                min={5}
                step="1"
                value={topUpAmount}
                onChange={(e) => setTopUpAmount(e.target.value)}
                className="mt-2 w-full px-4 py-3 bg-dark-700 border border-white/10 text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-burgundy-500"
              />
            </label>
            <p className="text-xs text-gray-400">{conversionText}</p>
            {estimatedTokens > 0 && (
              <div className="rounded-xl border border-emerald-500/30 bg-emerald-900/30 px-3 py-2 text-sm text-emerald-100">
                {estimatedLabel}
              </div>
            )}
            <button
              type="submit"
              disabled={plisioLoading}
              className="w-full px-4 py-3 rounded-xl bg-burgundy-600 hover:bg-burgundy-500 text-white font-semibold transition disabled:opacity-60"
            >
              {plisioLoading ? '...' : t.payButton}
            </button>
          </form>
          {plisioMessage && (
            <div
              className={`p-3 rounded-xl text-sm ${
                plisioMessage.type === 'error'
                  ? 'bg-red-900/30 text-red-200 border border-red-600/40'
                  : 'bg-green-900/30 text-green-200 border border-green-600/40'
              }`}
            >
              {plisioMessage.text}
            </div>
          )}
          <p className="text-xs text-gray-400">{t.minAmount}</p>
          <p className="text-gray-300 text-sm">{t.payDescription}</p>
          <div className="text-gray-400 text-sm">
            {t.promoBot}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PromoCode;

